<template>
  <div class="min-h-screen bg-neutral-50">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div class="mb-8 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 class="text-3xl font-bold mb-2">软件分享</h1>
          <p class="text-neutral-500">
            老师们分享的实用软件，提交后经审核上架
          </p>
        </div>
        <button
          @click="openSubmit"
          class="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90"
        >
          <i class="fa fa-plus mr-1"></i>分享软件
        </button>
      </div>

      <!-- 免责提示 -->
      <div
        class="mb-8 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3"
      >
        <i class="fa fa-info-circle text-amber-500 mt-0.5"></i>
        <p class="text-sm text-amber-800">
          本站软件资源由管理员整理发布，<strong>仅作分享交流使用</strong>，版权归原作者所有，请勿用于商业用途；下载后请自行查杀病毒，如涉及侵权请联系删除。
        </p>
      </div>

      <div v-if="loading" class="text-center py-20">
        <i class="fa fa-spinner fa-spin text-primary text-2xl"></i>
      </div>

      <div
        v-else-if="list.length === 0"
        class="text-center py-20 bg-white rounded-2xl border border-neutral-200"
      >
        <div
          class="inline-flex w-16 h-16 rounded-full bg-neutral-100 items-center justify-center mb-4"
        >
          <i class="fa fa-cube text-neutral-400 text-2xl"></i>
        </div>
        <h3 class="text-lg font-medium mb-1">还没有软件</h3>
        <p class="text-neutral-500 text-sm">
          管理员可在「管理后台 → 软件分享」中添加
        </p>
      </div>

      <div
        v-else
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
      >
        <div
          v-for="s in list"
          :key="s.id"
          class="bg-white rounded-2xl border border-neutral-200 p-5 hover:shadow-lg hover:border-primary transition-all duration-300 flex flex-col"
        >
          <div class="flex items-start mb-3">
            <div
              class="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0"
            >
              <img
                v-if="s.cover"
                :src="s.cover"
                class="w-full h-full object-cover"
              />
              <i v-else class="fa fa-cube text-neutral-400 text-xl"></i>
            </div>
            <div class="ml-3 min-w-0 flex-1">
              <h3 class="font-bold text-base mb-1 line-clamp-1">{{ s.title }}</h3>
              <p class="text-xs text-neutral-400">
                {{ s.version ? "v" + s.version : "" }}
                <span v-if="s.sizeText" class="ml-1">{{ s.sizeText }}</span>
                <span class="ml-1">{{ s.downloadCount || 0 }} 次下载</span>
              </p>
            </div>
            <button
              @click.stop="toggleMyApp(s.id)"
              class="ml-2 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
              :class="
                inMyApps(s.id)
                  ? 'text-primary bg-primary/10'
                  : 'text-neutral-400 hover:text-primary bg-neutral-100'
              "
              :title="
                inMyApps(s.id) ? '已加入我的应用，点击移出' : '加入我的应用'
              "
            >
              <i
                class="text-xs"
                :class="inMyApps(s.id) ? 'fa fa-check' : 'fa fa-plus'"
              ></i>
            </button>
          </div>

          <p class="text-sm text-neutral-500 line-clamp-3 flex-1 mb-3">
            {{ s.intro || "暂无简介" }}
          </p>

          <div class="flex flex-wrap gap-1 mb-4">
            <span
              class="px-2 py-0.5 rounded text-xs"
              :class="
                s.shareType === 'file'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-blue-50 text-blue-700'
              "
              >{{ s.shareType === "file" ? "站内附件" : s.panName || "网盘" }}</span
            >
            <span
              v-if="s.category"
              class="px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded text-xs"
              >{{ s.category }}</span
            >
            <span
              v-for="p in s.platformList || []"
              :key="p"
              class="px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded text-xs"
              >{{ platformLabel(p) }}</span
            >
          </div>

          <button
            @click="doDownload(s)"
            :disabled="downloadingId === s.id"
            class="w-full py-2 rounded-xl bg-primary/10 text-primary font-medium text-sm hover:bg-primary hover:text-white transition-colors disabled:opacity-60"
          >
            <i class="fa fa-download mr-1"></i>
            {{ downloadingId === s.id ? "获取中…" : "获取" }}
          </button>
          <p class="text-center text-[11px] text-neutral-400 mt-2">仅分享使用</p>
        </div>
      </div>
    </div>

    <!-- 分享软件（投稿，需审核） -->
    <div
      v-if="submitVisible"
      class="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4"
    >
      <div class="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-lg font-bold text-neutral-900">分享软件</h2>
          <button class="text-neutral-400 hover:text-neutral-600" @click="submitVisible = false">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <p class="text-xs text-neutral-500 mb-4">
          提交后进入审核，通过才会上架；审核意见会通过站内消息发给你。
        </p>

        <div class="space-y-3">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">软件名称 *</label>
              <input v-model="form.title" maxlength="60" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">分类</label>
              <input v-model="form.category" placeholder="教学工具 / 办公效率…" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">一句话简介</label>
            <textarea v-model="form.intro" rows="2" maxlength="200" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-none"></textarea>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">适用平台</label>
              <input v-model="form.platform" placeholder="windows,mac" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">版本</label>
              <input v-model="form.version" placeholder="如 2.1.0" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">大小</label>
              <input v-model="form.sizeText" placeholder="如 256MB" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">网盘平台</label>
              <input v-model="form.panName" placeholder="百度网盘 / 夸克网盘…" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">提取码</label>
              <input v-model="form.panCode" placeholder="可选" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">分享链接 *</label>
            <input v-model="form.panUrl" placeholder="https://pan.baidu.com/s/..." class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
          </div>

          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">封面图地址（可选）</label>
            <input v-model="form.cover" placeholder="https://..." class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-6">
          <button class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600" @click="submitVisible = false">取消</button>
          <button
            class="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? "提交中..." : "提交审核" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import myAppsMixin from "@/utils/myApps";
import { requireLoginPage } from "@/utils/authGuard";

export default {
  name: "SoftwarePage",
  mixins: [myAppsMixin("software")],
  data() {
    return {
      list: [],
      loading: false,
      downloadingId: "",
      submitVisible: false,
      saving: false,
      form: {
        id: "",
        title: "",
        intro: "",
        cover: "",
        category: "",
        platform: "",
        version: "",
        sizeText: "",
        shareType: "pan",
        panName: "百度网盘",
        panUrl: "",
        panCode: "",
      },
      platformOptions: [
        { value: "windows", label: "Windows" },
        { value: "mac", label: "macOS" },
        { value: "android", label: "安卓" },
        { value: "ios", label: "iOS" },
        { value: "web", label: "网页" },
      ],
    };
  },
  created() {
    this.getList();
  },
  methods: {
    /** 打开投稿弹层（需登录） */
    openSubmit() {
      if (!requireLoginPage(this, "分享软件")) return;
      this.form = {
        id: "",
        title: "",
        intro: "",
        cover: "",
        category: "",
        platform: "",
        version: "",
        sizeText: "",
        shareType: "pan",
        panName: "百度网盘",
        panUrl: "",
        panCode: "",
      };
      this.submitVisible = true;
    },

    async save() {
      if (!this.form.title.trim()) {
        ElMessage.warning("请填写软件名称");
        return;
      }
      if (!this.form.panUrl.trim()) {
        ElMessage.warning("请填写分享链接");
        return;
      }
      this.saving = true;
      try {
        const res = await this.$axios({
          url: "software/submit",
          method: "PUT",
          data: { data: await this.$aesEncrypt(this.form) },
        });
        if (res.code === 200) {
          ElMessage.success("已提交，等待审核");
          this.submitVisible = false;
        } else {
          ElMessage.error(res.msg || "提交失败");
        }
      } catch (e) {
        ElMessage.error("提交失败，请稍后重试");
      }
      this.saving = false;
    },

    platformLabel(v) {
      const p = this.platformOptions.find((x) => x.value === v);
      return p ? p.label : v;
    },
    getList() {
      this.loading = true;
      this.$axios({ url: "software/list", method: "GET" })
        .then((res) => {
          if (res.code === 200) {
            this.list = res.data || [];
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    async doDownload(item) {
      this.downloadingId = item.id;
      try {
        const data = await this.$aesEncrypt({ id: item.id });
        const res = await this.$axios({
          url: "software/download",
          method: "POST",
          data: { data },
        });
        if (res.code !== 200) {
          ElMessage.error(res.msg || "获取失败");
          return;
        }
        const url = res.data;
        if (!url) {
          ElMessage.warning("该软件暂未提供下载地址");
          return;
        }
        if (item.shareType === "file") {
          window.open(url, "_blank");
        } else {
          if (item.panCode) {
            try {
              await navigator.clipboard.writeText(item.panCode);
              ElMessage.success("提取码已复制：" + item.panCode);
            } catch (e) {
              ElMessage.info("提取码：" + item.panCode);
            }
          }
          window.open(url, "_blank");
        }
        // 刷新下载次数
        this.getList();
      } catch (err) {
        ElMessage.error("获取失败");
      } finally {
        this.downloadingId = "";
      }
    },
  },
};
</script>
